/**
 * Paper types, in one place.
 *
 * ---------------------------------------------------------------------------
 * WHY THIS FILE EXISTS
 *
 * The question editor originally gated every field on `track === 'foundation'`.
 * That was correct while foundation was the only ladder paper. When the Class
 * 5-10 and +1/+2 papers were added, those checks silently hid the question type,
 * the marks, the blanks, the answers and the entire options section — leaving a
 * form that could only ever produce "Answer 1 is empty", with no clue why.
 *
 * The server was never wrong; only the UI was. The failure was invisible in code
 * review because `=== 'foundation'` reads as deliberate everywhere it appears.
 *
 * So the list lives here, once, and both the page and the dialog import it. A
 * fourth ladder track is then a one-line change in one file rather than a hunt
 * through nine conditionals.
 *
 * The server ships the same list on `GET /assessment/subjects` as
 * `meta.tracks[].isLadder`; this is the client-side mirror for components that
 * hold a single subject rather than the whole tree.
 */

/**
 * Papers that are a subject ladder a student climbs, level by level.
 *
 * All three author IDENTICALLY — same question types, blanks, parallel answers,
 * flat/nested option layouts, marks and pass gates. Nothing in the editor should
 * distinguish between them.
 */
export const LADDER_TRACKS = ['foundation', 'secondary', 'higher_secondary'];

/** Is this paper a ladder? Use this, never a comparison against one track name. */
export const isLadderTrack = (track) => LADDER_TRACKS.includes(track);

/**
 * Full names, so a chip never has to fall back to a guess.
 *
 * The old fallback said "Foundation" for any track it did not name, which told
 * an editor working on the Class 9 paper that they were editing Class 1-4.
 */
export const TRACK_LABELS = {
  foundation: 'Foundation · Class 1-4',
  secondary: 'Secondary · Class 5-10',
  higher_secondary: 'Higher Secondary · +1, +2',
  kg: 'KG picture paper',
  parent: 'Parent questionnaire',
};

/** Icon per paper type for the navigation tree. */
export const TRACK_META = {
  foundation: { icon: 'mdi:school-outline', color: 'primary' },
  secondary: { icon: 'mdi:book-open-variant', color: 'primary' },
  higher_secondary: { icon: 'mdi:school', color: 'primary' },
  kg: { icon: 'mdi:teddy-bear', color: 'secondary' },
  parent: { icon: 'mdi:account-child-outline', color: 'info' },
};
